import { ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import type { SSEMessage } from '@/_shared/types';

type SkillInvokedMessage = Extract<SSEMessage, { type: 'skill_invoked' }>;

interface SkillInvokedChipProps {
  message: SkillInvokedMessage;
}

/**
 * Renders a compact inline chip for a slash-invoked skill.
 *
 * Displays the skill title from the SSE row snapshot — reads from
 * `message.title` only. Never re-fetches from context_entries, so the
 * chip renders correctly even after a skill is unpromoted or deleted
 * (Named Invariant #3: display row is display-only).
 *
 * Expanded state shows a note that the body was injected into context;
 * the body itself is not stored in the SSE row and cannot be shown.
 */
export function SkillInvokedChip({ message }: SkillInvokedChipProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mx-4 my-1" data-testid="skill-invoked-chip">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 text-left"
        aria-expanded={expanded}
      >
        <Badge variant="secondary" className="cursor-pointer gap-1 text-xs">
          <Zap className="size-3" aria-hidden="true" />
          Skill: {message.title}
          {expanded ? (
            <ChevronUp className="size-3" aria-hidden="true" />
          ) : (
            <ChevronDown className="size-3" aria-hidden="true" />
          )}
        </Badge>
      </button>
      {expanded && (
        <p className="mt-1 ml-1 text-xs text-muted-foreground italic">Skill body was injected into chat context.</p>
      )}
    </div>
  );
}
